import { useId, type ReactNode } from 'react';
import { cx } from '../ui/ui';

/**
 * Einheitlicher Abschnitt in den Kartendetails: weiße Fläche mit 1-px-Kontur und runden Ecken auf
 * leicht abgesetztem Grund; Kopf mit Überschrift links, Aktion oder Zähler rechts, darunter eine
 * dezente Trennlinie. Nicht verschachteln.
 */
export function Section({
  title,
  icon,
  aside,
  id,
  children,
  bodyClass,
}: {
  title: ReactNode;
  icon?: ReactNode;
  /** Aktion oder Zähler rechts im Kopf */
  aside?: ReactNode;
  /** Sprungziel (z. B. aus dem Arbeitsstand); bekommt dann den Fokus */
  id?: string;
  children?: ReactNode;
  bodyClass?: string;
}) {
  const hid = useId();
  return (
    <section
      aria-labelledby={hid}
      id={id}
      tabIndex={id ? -1 : undefined}
      className="scroll-mt-4 rounded-xl border border-[var(--line)] bg-[var(--panel)] outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
    >
      <div className="flex min-h-12 items-center gap-2 border-b border-[var(--line)] py-1.5 pr-2 pl-4">
        <h3 id={hid} className="flex min-w-0 flex-1 items-center gap-1.5 text-[15px] font-semibold">
          {icon && (
            <span className="shrink-0 text-[var(--ink-soft)]" aria-hidden>
              {icon}
            </span>
          )}
          {title}
        </h3>
        {aside}
      </div>
      {children !== undefined && children !== null && children !== false && <div className={cx('flex flex-col gap-3 p-4', bodyClass)}>{children}</div>}
    </section>
  );
}

/** Kleine Aktion im Abschnittskopf (Text, optional mit Symbol) */
export function SectionAction({ children, onClick, label }: { children: ReactNode; onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-[var(--ink-soft)] hover:bg-black/5 hover:text-[var(--ink)] sm:min-h-9 dark:hover:bg-white/10"
    >
      {children}
    </button>
  );
}
